import { Map } from 'immutable'
import _get from 'lodash/get'
import { addNewRelicAction } from '../app/_newrelic'
import { getBaseNewRelicData } from '../adyen/index'
import {
  createCheckoutV2Order,
  captureEmail,
  formatCheckoutV2OrderQuery,
  getCheckoutMarketingTrackingData,
  CHECKOUT_ORDER_ERROR_TYPE_UNKNOWN,
} from './index'
import './types'

export const SET_CHECKOUT_ACCOUNT_VALID = 'SET_CHECKOUT_ACCOUNT_VALID'
export const SET_CHECKOUT_ACCOUNT_CREATE_DATA = 'SET_CHECKOUT_ACCOUNT_CREATE_DATA'
export const SET_CHECKOUT_USER_DATA = 'SET_CHECKOUT_USER_DATA'
export const SET_CHECKOUT_ORDER_PROCESSING = 'SET_CHECKOUT_ORDER_PROCESSING'
export const SET_CHECKOUT_ORDER_DATA = 'SET_CHECKOUT_ORDER_DATA'
export const SET_CHECKOUT_ORDER_COMPLETE = 'SET_CHECKOUT_ORDER_COMPLETE'
export const SET_CHECKOUT_ORDER_ERROR = 'SET_CHECKOUT_ORDER_ERROR'
export const SET_CHECKOUT_EMAIL_CAPTURE_STATUS = 'SET_CHECKOUT_EMAIL_CAPTURE_STATUS'
export const RESET_CHECKOUT_EMAIL_CAPTURE_STATUS = 'RESET_CHECKOUT_EMAIL_CAPTURE_STATUS'
export const SET_CHECKOUT_STEP = 'SET_CHECKOUT_STEP'
export const SET_CHECKOUT_EVENT_STEP = 'SET_CHECKOUT_EVENT_STEP'
export const RESET_CHECKOUT = 'RESET_CHECKOUT'
export const SET_CHECKOUT_PLAN_SELECTION = 'SET_CHECKOUT_PLAN_SELECTION'
export const SET_CHECKOUT_REACTIVATION = 'SET_CHECKOUT_REACTIVATION'
export const SET_CHECKOUT_ORDER_STATUS = 'SET_CHECKOUT_ORDER_STATUS'
export const SET_CHECKOUT_INITIATED = 'SET_CHECKOUT_INITIATED'
export const SET_CHECKOUT_PLAN_PRE_SELECTED = 'SET_CHECKOUT_PLAN_PRE_SELECTED'
export const SET_CHECKOUT_CREATION_SOURCE = 'SET_CHECKOUT_CREATION_SOURCE'
export const SET_CHECKOUT_SINGLE_PAGE_STEP = 'SET_CHECKOUT_SINGLE_PAGE_STEP'

// Winback flow
export const SET_CHECKOUT_FLOW = 'SET_CHECKOUT_FLOW'
export const SET_CHECKOUT_WINBACK_USE_DEFAULT_PAYMENT = 'SET_CHECKOUT_WINBACK_USE_DEFAULT_PAYMENT'
export const SET_CHECKOUT_WINBACK_STEP = 'SET_CHECKOUT_WINBACK_STEP'
export const SET_CHECKOUT_WINBACK_LOADING = 'SET_CHECKOUT_WINBACK_LOADING'

export const SET_CHECKOUT_DISCOUNT_CODE = 'SET_CHECKOUT_DISCOUNT_CODE'
export const SET_CHECKOUT_DISCOUNT_ELIGIBILITY = 'SET_CHECKOUT_DISCOUNT_ELIGIBILITY'
export const SET_CHECKOUT_DISCOUNT_LOADING = 'SET_CHECKOUT_DISCOUNT_LOADING'

export function processCheckoutOrder (options) {
  const {
    paymentInfo,
    tracking,
    auth,
    startDateOverrideInfo,
    reactivation,
    emailOptIn,
    fbEventId,
    velocityCheckToken,
    useDefaultPaymentMethod = false,
  } = options
  return async function processCheckoutOrderThunk (dispatch, getState) {
    // do async checkout
    dispatch(setCheckoutOrderProcessing(true))

    try {
      const marketingTrackingData = getCheckoutMarketingTrackingData(getState())
      const orderV2 = await createCheckoutV2Order({
        paymentInfo,
        tracking,
        auth,
        startDateOverrideInfo,
        reactivation,
        emailOptIn,
        fbEventId,
        velocityCheckToken,
        useDefaultPaymentMethod,
        marketingTrackingData,
      })
      const checkoutId = _get(orderV2, 'checkoutId')

      if (checkoutId) {
        dispatch(setCheckoutOrderStatus(checkoutId))
      } else {
        try {
          const state = getState ? getState() : {}
          const payment = state.payment || Map()
          const plans = state.plans || Map()
          const baseNewRelicData = getBaseNewRelicData(state)
          const paymentType = payment.getIn(['data', 'payments', 'data', 'paymentMethod', 'type'], '')
          const planSku = plans.getIn(['selection', 'sku'])
          const paymentResultCode = payment.getIn(['data', 'payments', 'data', 'resultCode'])
          const paymentSuccess = payment.getIn(['data', 'payments', 'success'])
          const queryData = formatCheckoutV2OrderQuery(
            { startDateOverrideInfo, velocityCheckToken },
          )

          addNewRelicAction(
            'CheckoutFlowOrderNoCheckoutId',
            {
              ...baseNewRelicData,
              paymentType,
              planSku,
              paymentTokenResultCode: paymentResultCode,
              paymentTokenSuccess: paymentSuccess,
              orderSuccess: _get(orderV2, 'success'),
              checkoutId: _get(orderV2, 'checkoutId'),
              orderResponse: JSON.stringify(orderV2),
              status: _get(orderV2, 'status'),
              queryData,
            },
          )
        } catch (_) {
          // avoid affecting flow if New Relic logging fails
        }
        dispatch(setCheckoutOrderError(CHECKOUT_ORDER_ERROR_TYPE_UNKNOWN))
      }

      // we need to return the orderV2 object to the caller
      return orderV2
    } catch (e) {
      try {
        const state = getState ? getState() : {}
        const payment = state.payment || Map()
        const plans = state.plans || Map()
        const baseNewRelicData = getBaseNewRelicData(state)
        const paymentType = payment.getIn(['data', 'payments', 'data', 'paymentMethod', 'type'], '')
        const planSku = plans.getIn(['selection', 'sku'])
        const paymentResultCode = payment.getIn(['data', 'payments', 'data', 'resultCode'])
        const paymentSuccess = payment.getIn(['data', 'payments', 'success'])
        const queryData = formatCheckoutV2OrderQuery(
          { startDateOverrideInfo, velocityCheckToken },
        )

        let errorMessage
        try {
          errorMessage = typeof e?.message === 'string' ? e.message : JSON.stringify(e)
        } catch (_) {
          errorMessage = String(e)
        }
        addNewRelicAction(
          'CheckoutFlowOrderError',
          {
            ...baseNewRelicData,
            paymentType,
            planSku,
            paymentTokenResultCode: paymentResultCode,
            paymentTokenSuccess: paymentSuccess,
            errorMessage,
            queryData,
            errorStack: e?.stack,
            errorName: e?.name,
          },
        )
      } catch (_) {
        // avoid masking the original order error if New Relic logging fails
      }
      dispatch(setCheckoutOrderError(CHECKOUT_ORDER_ERROR_TYPE_UNKNOWN))
      throw e
    }
  }
}

export function captureCheckoutEmail (options) {
  return async function captureCheckoutEmailThunk (dispatch) {
    try {
      const res = await captureEmail(options)
      const { success, errorCode } = res
      dispatch(setCheckoutEmailCaptureStatus(success, errorCode))
    } catch (e) {
      dispatch(setCheckoutEmailCaptureStatus(false, null))
    }
  }
}

export function setCheckoutAccountValid (value) {
  return {
    type: SET_CHECKOUT_ACCOUNT_VALID,
    payload: value,
  }
}

export function setCheckoutOrderError (data) {
  return {
    type: SET_CHECKOUT_ORDER_ERROR,
    payload: data,
  }
}

export function setCheckoutAccountCreateData (data) {
  return {
    type: SET_CHECKOUT_ACCOUNT_CREATE_DATA,
    payload: data,
  }
}

export function setCheckoutUserData (data) {
  return {
    type: SET_CHECKOUT_USER_DATA,
    payload: data,
  }
}

export function setCheckoutOrderData (data) {
  return {
    type: SET_CHECKOUT_ORDER_DATA,
    payload: data,
  }
}

export function setCheckoutOrderProcessing (data) {
  return {
    type: SET_CHECKOUT_ORDER_PROCESSING,
    payload: data,
  }
}

export function setCheckoutOrderComplete (data) {
  return {
    type: SET_CHECKOUT_ORDER_COMPLETE,
    payload: data,
  }
}

export function setCheckoutEmailCaptureStatus (status, errorCode) {
  return {
    type: SET_CHECKOUT_EMAIL_CAPTURE_STATUS,
    payload: { status, errorCode },
  }
}

export function resetCheckoutEmailCaptureStatus () {
  return {
    type: RESET_CHECKOUT_EMAIL_CAPTURE_STATUS,
  }
}

export function setCheckoutStep (value) {
  return {
    type: SET_CHECKOUT_STEP,
    payload: value,
  }
}

export function setCheckoutEventStep (value) {
  return {
    type: SET_CHECKOUT_EVENT_STEP,
    payload: value,
  }
}

export function resetCheckout () {
  return {
    type: RESET_CHECKOUT,
  }
}

export function setPlanSelection (payload) {
  return {
    type: SET_CHECKOUT_PLAN_SELECTION,
    payload,
  }
}

export function setCheckoutReactivation (value) {
  return {
    type: SET_CHECKOUT_REACTIVATION,
    payload: value,
  }
}

export function setCheckoutOrderStatus (id, attempts = 0) {
  return {
    type: SET_CHECKOUT_ORDER_STATUS,
    payload: { id, attempts },
  }
}

export function setCheckoutInitiated (initiated) {
  return {
    type: SET_CHECKOUT_INITIATED,
    payload: initiated,
  }
}

export function setCheckoutPlanPreSelected (value) {
  return {
    type: SET_CHECKOUT_PLAN_PRE_SELECTED,
    payload: value,
  }
}

export function setCheckoutCreationSource (value) {
  return {
    type: SET_CHECKOUT_CREATION_SOURCE,
    payload: value,
  }
}

export function setCheckoutSinglePageStep (step, eventStep) {
  return {
    type: SET_CHECKOUT_SINGLE_PAGE_STEP,
    payload: { step, eventStep },
  }
}

export function setCheckoutFlow (data) {
  return {
    type: SET_CHECKOUT_FLOW,
    payload: data,
  }
}

export function setCheckoutWinbackUseDefaultPayment (data) {
  return {
    type: SET_CHECKOUT_WINBACK_USE_DEFAULT_PAYMENT,
    payload: data,
  }
}

export function setCheckoutWinbackStep (data) {
  return {
    type: SET_CHECKOUT_WINBACK_STEP,
    payload: data,
  }
}

export function setCheckoutDiscountCode (code) {
  return {
    type: SET_CHECKOUT_DISCOUNT_CODE,
    payload: code,
  }
}

export function setCheckoutDiscountEligibility (data) {
  return {
    type: SET_CHECKOUT_DISCOUNT_ELIGIBILITY,
    payload: data,
  }
}

export function setCheckoutDiscountLoading (loading) {
  return {
    type: SET_CHECKOUT_DISCOUNT_LOADING,
    payload: loading,
  }
}
